// ============================================
// SMS 추천 핸들러 팩토리
// ============================================
// useAiChat에서 SMS 관련 함수를 분리하여 관리
//
// 구조:
//   [1] 저장/복원 유틸   — buildSmsForSave, restoreSmsFromSave
//   [2] createSmsHandlers — 의존성 주입 후 생성/발송/저장 핸들러 반환
//
// ──────────────────────────────────────────────
// 전체 흐름 (생성 → 렌더링 → 저장 → 복원)
// ──────────────────────────────────────────────
//
// 1) 생성
//    useAiChat.runAgent → recommend_sms 툴 호출
//    → generateSmsTemplates(messageId, toolResult) 호출
//    → FAST_MODEL로 문자 템플릿 생성
//    → updateAiMessage로 chatMessages에 smsRecommendation 첨부
//    → pendingSmsRef에 임시 저장 → 다음 턴에서 messageFlags로 전달
//
// 2) 렌더링
//    DisplayRenderer → smsRecommendation prop → SmsRecommendPanel
//    - bulk 모드  : BulkSmsSection (템플릿 카드 + 발송 버튼)
//    - individual : InlineSmsCell (테이블 셀 내 문자) + InlineSmsSendCell (발송 버튼)
//
// 3) 저장 (세션 전환 시 소실 방지)
//    - 최초 저장: useAiChat.saveSession → buildSmsForSave로 경량화 → DB 저장
//    - 발송 후 저장: handleSmsSend/handleIndividualSmsSend → updateConversationSms
//
// 4) 복원
//    useAiChat.loadConversations → JSON 파싱 → restoreSmsFromSave로 런타임 형식 복원
//
// ──────────────────────────────────────────────
// 모드별 데이터 구조 (smsRecommendation)
// ──────────────────────────────────────────────
//
// [bulk 모드]
//   { type, mode:'bulk', recipients:[{memberId,name,phone}],
//     templates:[{id,text,status}], individualTexts:{}, status, label, context }
//
// [individual 모드]
//   { type, mode:'individual', recipients:[{memberId,name,phone}],
//     individualTexts:{ [memberId]:{text,status} }, templates:[], status, label, context }
//
// ──────────────────────────────────────────────
// 저장/복원 시 변환 규칙
// ──────────────────────────────────────────────
//
// buildSmsForSave (런타임 → DB)
//   - bulk: templates[].text → content 키 변경, recipients → 건수(number)로 경량화
//   - individual: individualTexts 그대로 유지
//   - 공통: recipients 배열은 {memberId, name, phone}만 추출
//
// restoreSmsFromSave (DB → 런타임)
//   - bulk: templates[].content → text로 역변환
//   - individual: individualTexts 그대로 복원
//   - 공통: status 그대로 유지 (sent/completed면 UI에서 발송 버튼 비활성화)
//
// ──────────────────────────────────────────────
// SMS Status 상태 지표
// ──────────────────────────────────────────────
//
// ┌────────────────┬─────────────────────────────────────────┬──────────┐
// │ Status         │ 설명                                     │ 진행상황   │
// ├────────────────┼─────────────────────────────────────────┼──────────┤
// │ loading        │ 템플릿/문자 생성 중                           │ 진행 중  │
// │ ready          │ 생성 완료, 발송 가능                          │ 준비됨   │
// │ sending        │ 발송 중                                    │ 진행 중  │
// │ sent           │ 발송 완료                                  │ 완료     │
// │ unsent         │ 발송하지 않음 (다른 템플릿 선택)                │ 건너뜀   │
// │ error          │ 생성/발송 실패                              │ 오류     │
// │ completed      │ SMS 추천 전체 프로세스 완료                   │ 완료     │
// └────────────────┴─────────────────────────────────────────┴──────────┘
//
// 상태 흐름:
//   생성: loading → ready (또는 error)
//   발송: ready → sending → sent (또는 error)
//   무시: ready → unsent
//   결과: (sent|unsent|error) → completed

import FetchModule from '../../Model/Share/Network/FetchModule';
import { parseJsonSafely } from '../Utils/aiUtils';
import { SMS_TEMPLATE_PROMPT } from './aiPromptBuilders';
import { AI_MODELS, TOOL_CALLING_LOG } from '../Model/aiConfig';

const { FAST_MODEL } = AI_MODELS;

// ============================================
// [1] 저장/복원 유틸 (순수 함수)
// ============================================

// 런타임 smsRecommendation → DB 저장 형식 변환 //
export function buildSmsForSave(smsRec) {
  if (!smsRec) return null;

  const base = {
    type: smsRec.type,
    mode: smsRec.mode,
    status: smsRec.status,
    recipients: smsRec.recipients,
    individualTexts: smsRec.individualTexts,
    templates: smsRec.templates,
    label: smsRec.label,
    context: smsRec.context,
  };

  if (smsRec.mode === 'individual') {
    // recipients + individualTexts (객체) 유지 //
    base.recipients = (smsRec.recipients || []).map(r => ({
      memberId: r.memberId,
      name: r.name,
      phone: r.phone || '',
    }));
    base.individualTexts = smsRec.individualTexts || {};
  }
  else {
    // bulk 모드 — template별 status/content/recipients 저장 //
    const recipientCount = (smsRec.recipients || []).length;
    base.templates = (smsRec.templates || []).map(t => ({
      id: t.id,
      content: t.text || t.content || '',
      status: t.status || 'ready',
      recipients: recipientCount,
    }));
    base.recipients = (smsRec.recipients || []).map(r => ({
      memberId: r.memberId,
      name: r.name,
      phone: r.phone || '',
    }));
  }

  return base;
}

// DB 저장 형식 → 런타임 smsRecommendation 복원 //
export function restoreSmsFromSave(saved) {
  if (!saved) return null;

  // 기본 필드 모두 초기화
  const restored = {
    type: saved.type,
    mode: saved.mode,
    status: saved.status,
    label: saved.label,
    context: saved.context,
    recipients: [],
    individualTexts: {},
    templates: [],
  };

  if (saved.mode === 'individual') {
    // individual 모드 — 객체 형식 유지 //
    restored.recipients = saved.recipients || [];
    restored.individualTexts = saved.individualTexts || {};
  }
  else {
    // bulk 모드 — content → text 변환 //
    restored.templates = (saved.templates || []).map(t => ({
      id: t.id,
      text: t.content || t.text || '',
      status: t.status || 'ready',
    }));
    restored.recipients = saved.recipients || [];
  }

  return restored;
}

// ============================================
// [2] SMS 핸들러 팩토리
// ============================================
// useAiChat에서 의존성을 주입받아 핸들러 함수들을 생성
//
// 반환 함수 목록:
//   저장:  updateConversationSms
//   생성:  generateSmsTemplates, handleIndividualSmsGenerate
//   발송:  handleSmsSend, handleIndividualSmsSend

export function createSmsHandlers({
  clientRef,
  storeName,
  sessionId,
  chatMessagesRef,
  conversationMetaRef,
  updateAiMessage,
  loggedCompletion,
  resendTurnWithSms,
}) {
  // ===== 저장 ===== //
  // SMS 비동기 완료 후 턴 재전송 (P2c — 구 ai-log/conversation 자체 POST 폐기, C4 이원 UPSERT 종결) //
  function updateConversationSms(messageId, smsRecOverride) {
      const meta = conversationMetaRef.current[messageId];
      if (!meta) return;

      const msg = chatMessagesRef.current.find(m => m.id === messageId);
      if (!msg) return;

      const EXTRA_SAVE_FIELDS = ['memberId', 'profile', 'phone', 'phoneNumber', 'tel', 'mobile', 'contactNumber'];
      const saveObj = { text: msg.text || '' };

      if (msg.displayItems) {
        const { columns, items } = msg.displayItems;
        const saveFields = [...new Set([...(columns || []), ...EXTRA_SAVE_FIELDS])];
        saveObj.displayItems = {
          ...msg.displayItems,
          items: (items || []).map(item => {
            const lite = {};
            for (const field of saveFields) {
              if (item[field] !== undefined) lite[field] = item[field];
            }
            return lite;
          }),
        };
      }

      saveObj.smsRecommendation = buildSmsForSave(smsRecOverride);

      // 턴 idempotent 재전송 — renderJson(화면 재현) + SMS llmCalls 갱신은 useAiChat이 처리 //
      if (typeof resendTurnWithSms === 'function') {
        resendTurnWithSms(meta.conversationId, JSON.stringify(saveObj), saveObj.smsRecommendation);
      }
    }

  // ===== 생성 ===== //
  // SMS 템플릿 생성 (FAST_MODEL) //
  async function generateSmsTemplates(messageId, toolResult) {
    const client = clientRef.current;
    if (!client) return null;

    const { mode, targetDescription, recipients, tone, contentGuide } = toolResult;
    // [SMS_TARGET_GENERIC_FALLBACK] L12-AGREE-001 cycle 1 (2026-05-31)
    //   기존 'GENERAL' literal default가 SMS prompt에 영문 leak → LLM 출력 품질 저하.
    //   targetDescription 빠진 경우 빈 문자열로 default — prompt 자체가 자연스럽게 "회원 N명에게..." phrasing 유지.
    // [L18-SMS-FILTERED-001 cycle 1 (2026-05-31)]
    //   LLM이 targetDescription에 intro phrase "여자 회원 7명" 같은 count-suffixed value를 넘기면
    //   prompt가 "여자 회원 7명 회원 ${memberCount}명에게..." duplicate phrasing → LLM이 어색한 SMS body 출력.
    //   해소: trailing count/명 + 중복 '회원' 제거하는 generic sanitizer (test mock dual sync — nodeAgent.mjs 동일).
    // [L6-SMS-CONTENT-SPEC-001 cycle 1 (운영 5차 rid80)]
    //   targetDescription은 SMS body prompt에 "${X} 회원 N명에게…"로 박혀 고객 발송 문안 본문에 직접 노출된다.
    //   LLM이 여기에 타게팅/선별 메타("마지막 3명 제외", "N명 빼고", "상위 N명만")를 섞어 넘기면
    //   그 내부 조건이 고객 문안에 그대로 누출된다(운영 rid80 본질 ②). targetDescription은 "누구인지"(만료 예정 회원)만
    //   담아야 하고 "어떻게 추렸는지"(제외/빼고/상위·하위·처음·마지막 N) 메타는 본문에서 격리해야 한다.
    //   범용: case ID / 특정 리터럴 string-match 아님 — exclusion/positional 선별 메타 keyword class를 절(clause) 단위로 strip.
    const _stripTargetingMeta = (s) => {
      if (!s || typeof s !== 'string') return s || '';
      let out = s;
      // 위치 기반 선별: 처음/마지막/상위/하위/끝/앞/뒤 + (선택) N명/N개 — exclusion 동반 여부 무관 제거
      out = out.replace(/\s*(맨\s*)?(처음|마지막|상위|하위|끝|앞|뒤)\s*\d*\s*(명|개|분|건|번째)?\s*(은|는|을|를|만|빼고|제외(하고|한|하는)?)?/gu, ' ');
      // 제외/빼고 동사 절: "X 제외(하고/한/하는)" / "X 빼고/빼줘/빼서" — 메타 동사만 제거
      out = out.replace(/\s*\d*\s*(명|개|분|건)?\s*(을|를|은|는)?\s*(제외(하고|한|하는|해|해서|해줘)?|빼고|빼줘|빼서|빼)\s*/gu, ' ');
      // count-only narrowing: "N명만" / "N명한테만" (선별 한정 메타)
      out = out.replace(/\s*\d+\s*(명|분|건)\s*(한테만|에게만|만)\s*/gu, ' ');
      // dangling connector 정리 — 메타 제거 후 떠도는 연결어("중/의/에서") + 고립 "대상" + 빈 절(,,) 정리
      out = out.replace(/\s{2,}/g, ' ').trim();
      for (let i = 0; i < 4; i++) {
        const b = out;
        out = out.replace(/\s*(중|의|에서|에게|에)\s*$/u, '').trim();
        out = out.replace(/\s*(중|의)\s+대상\s*$/u, '').trim();   // "X 중 대상" → "X"
        out = out.replace(/\s*대상\s*$/u, '').trim();              // 고립 trailing "대상"
        if (out === b) break;
      }
      out = out.replace(/\s*[,，、]\s*(?=[,，、])/g, '').replace(/[\s,，、]+$/g, '').replace(/^[\s,，、]+/g, '');
      out = out.replace(/\s{2,}/g, ' ').trim();
      return out;
    };
    const _sanitizeTargetPhrase = (raw) => {
      if (!raw || typeof raw !== 'string') return '';
      let s = _stripTargetingMeta(raw.trim());
      s = s.replace(/[.,。、\s]+$/, '');
      for (let i = 0; i < 5; i++) {
        const before = s;
        s = s.replace(/\s*\d+\s*(명|분|건|회원)(에게|한테|들|님)?\s*$/u, '');
        s = s.replace(/\s*(회원|고객|선생님)\s*$/u, '');
        s = s.replace(/(회원|고객|선생님)(\s+\1)+/gu, '$1');
        s = s.trim();
        if (s === before) break;
      }
      return s || '';
    };
    const smsType = (typeof targetDescription === 'string' && targetDescription.trim())
      ? _sanitizeTargetPhrase(targetDescription)
      : '';
    // [L6-SMS-CONTENT-SPEC-001 본질 ① / 운영 5차 rid80] 사용자 지정 본문 내용(만료 안내·재등록·응원)을 FAST_MODEL 프롬프트에 반영.
    //   contentGuide도 본질 ② 방어 대상 — LLM이 타게팅 메타("마지막 3명 제외")를 content에 섞으면 본문 누출되므로 동일 sanitizer 적용.
    //   테마 문구 전용이므로 trailing count/'회원' 정리는 불필요 → _stripTargetingMeta만(선별 메타 절 제거).
    const smsContent = (typeof contentGuide === 'string' && contentGuide.trim())
      ? _stripTargetingMeta(contentGuide)
      : '';
    const prompt = mode === 'individual'
      ? SMS_TEMPLATE_PROMPT.individual(storeName, smsType, recipients, tone, smsContent)
      : SMS_TEMPLATE_PROMPT.bulk(storeName, smsType, recipients.length, tone, smsContent);

    // 템플릿 생성 중 상태로 업데이트 //
    updateAiMessage(messageId, undefined, {
      smsRecommendation: {
        type: smsType,
        mode,
        recipients,
        status: 'loading',
      },
    });

    try {
      // [시임1] sms_bulk — loggedCompletion 경유 (토큰 포착, 비-aiFetch 직접 호출 동작 보존) //
      const response = await loggedCompletion('sms_bulk', {
        model: FAST_MODEL,
        messages: [{ role: 'user', content: prompt }],
        temperature: 0.7,
        max_completion_tokens: 2000,
      });

      const content = response.choices[0]?.message?.content;
      const templates = parseJsonSafely(content);

      if (TOOL_CALLING_LOG) console.log('[SMS] Templates generated:', templates);

      if (!Array.isArray(templates)) {
        updateAiMessage(messageId, undefined, {
          smsRecommendation: { status: 'error' },
        });
        return null;
      }

      if (mode === 'individual') {
        // 개별 모드: name으로 memberId 매칭
        const individualTexts = {};

        for (const tpl of templates) {
          const matched = recipients.find(r => r.name === tpl.name);
          if (matched) {
            individualTexts[matched.memberId] = { text: tpl.text, status: 'ready' };
          }
        }

        const result = {
          type: smsType,
          mode,
          recipients,
          individualTexts,
          templates: [],
          status: 'ready',
          label: toolResult.label,
          context: toolResult.context,
        };
        updateAiMessage(messageId, undefined, {
          smsRecommendation: result,
        });
        return result;
      }

      // 일괄 모드: 템플릿 배열
      const result = {
        type: smsType,
        mode,
        recipients,
        templates: templates.map((t, i) => ({ id: `tpl_${i}`, text: t.text, status: 'ready' })),
        individualTexts: {},
        status: 'ready',
        label: toolResult.label,
        context: toolResult.context,
      };

      updateAiMessage(messageId, undefined, {
        smsRecommendation: result,
      });
      return result;
    }
    catch (error) {
      if (TOOL_CALLING_LOG) console.error('[SMS] Template generation error:', error);
      updateAiMessage(messageId, undefined, {
        smsRecommendation: { status: 'error' },
      });
      return null;
    }
  }

  // SMS 개별 문자 생성 (individual 모드 — "문자 생성 하기" 버튼, 삭제 보류) //
  const handleIndividualSmsGenerate = async (messageId, recipient, context) => {
    if (TOOL_CALLING_LOG) console.log('[SMS] Individual generate:', { messageId, memberId: recipient?.memberId });

    const client = clientRef.current;
    if (!client || !recipient) return;

    // loading 상태 세팅
    updateAiMessage(messageId, undefined, {
      smsRecommendation: prev => ({
        ...prev,
        individualTexts: {
          ...prev.individualTexts,
          [recipient.memberId]: { text: '', status: 'loading' },
        },
      }),
    });

    try {
      // [SMS_TARGET_GENERIC_FALLBACK] L12-AGREE-001 cycle 1 (2026-05-31) — same fallback as generateSmsTemplates above
      const _individualSmsType = (typeof (context?.type || context?.smsType) === 'string' && (context.type || context.smsType).trim())
        ? (context.type || context.smsType).trim()
        : '';
      const prompt = SMS_TEMPLATE_PROMPT.individual(storeName, _individualSmsType, [recipient], context?.tone);
      // [시임1] sms_individual — loggedCompletion 경유 (토큰 포착, 비-aiFetch 직접 호출 동작 보존) //
      const response = await loggedCompletion('sms_individual', {
        model: FAST_MODEL,
        messages: [{ role: 'user', content: prompt }],
        temperature: 0.7,
        max_completion_tokens: 500,
      });

      const content = response.choices[0]?.message?.content;
      const templates = parseJsonSafely(content);
      const generatedText = Array.isArray(templates) ? templates[0]?.text : '';

      updateAiMessage(messageId, undefined, {
        smsRecommendation: prev => ({
          ...prev,
          individualTexts: {
            ...prev.individualTexts,
            [recipient.memberId]: { text: generatedText || '', status: 'ready' },
          },
        }),
      });
    }
    catch (error) {
      if (TOOL_CALLING_LOG) console.error('[SMS] Individual generate error:', error);
      updateAiMessage(messageId, undefined, {
        smsRecommendation: prev => ({
          ...prev,
          individualTexts: {
            ...prev.individualTexts,
            [recipient.memberId]: { text: '', status: 'error' },
          },
        }),
      });
    }
  };

  // ===== 발송 ===== //
  // 발송 content placeholder 방어 정규화 — message/send_target은 per-member 치환을 하지 않으므로 클라이언트에서 처리 //
  //   {center_name}/{store_name} → 센터명, {member_name}/{user_name} → 회원명(없으면 "회원님"). 템플릿이 실제 값을 박아넣어 보통 no-op이나, placeholder 잔존 시 그대로 발송되는 것을 차단.
  const normalizeSmsContent = (content, memberName) => {
    if (!content || typeof content !== 'string') return content || '';

    return content
      .replace(/\{center_name\}|\{store_name\}/g, storeName || '')
      .replace(/\{member_name\}|\{user_name\}/g, memberName || '회원님');
  };

  // SMS 일괄 발송 (bulk 모드) //
  const handleSmsSend = (messageId, templateId, templateText, selectedRecipients) => {
    if (TOOL_CALLING_LOG) console.log('[SMS] Send:', { messageId, templateId, templateText, recipientCount: selectedRecipients?.length });

    // 발송 중 상태로 업데이트 //
    updateAiMessage(messageId, undefined, {
      smsRecommendation: prev => ({
        ...prev,
        status: 'sending',
        templates: (prev.templates || []).map(t => ({
          ...t,
          status: t.id === templateId ? 'sending' : t.status,
        })),
      }),
    });

    // 발송 실패 → 에러 상태 //
    const setSendError = () => {
      updateAiMessage(messageId, undefined, {
        smsRecommendation: prev => ({
          ...prev,
          status: 'error',
          templates: (prev.templates || []).map(t => ({
            ...t,
            status: t.id === templateId ? 'error' : t.status,
          })),
        }),
      });
    };

    // SMS 발송 API 호출 — message/send_target (title, content, memberIds) //
    const memberIds = (selectedRecipients || []).map(r => r.memberId).filter(id => id != null);
    const fetchModule = new FetchModule();
    (
      async () => {
        try {
          const responseData = await fetchModule.postDataVersion2('message/send_target', 'POST', {
            title: storeName,
            content: normalizeSmsContent(templateText),
            memberIds,
          });

          if (responseData.status === 200) {
            // 발송 성공 → 상태 업데이트 //
            updateAiMessage(messageId, undefined, {
              smsRecommendation: prev => ({
                ...prev,
                status: 'completed',
                templates: (prev.templates || []).map(t => ({
                  ...t,
                  status: t.id === templateId ? 'sent' : 'unsent',
                })),
              }),
            });

            // conversation-log 저장 //
            const currentSms = chatMessagesRef.current.find(m => m.id === messageId)?.smsRecommendation;
            const smsToSave = currentSms ? {
              ...currentSms,
              status: 'completed',
              templates: (currentSms.templates || []).map(t => ({
                ...t,
                status: t.id === templateId ? 'sent' : 'unsent',
              })),
            } : null;

            if (smsToSave) {
              updateConversationSms(messageId, smsToSave);
            }
          }
          else setSendError();
        }
        catch (error) {
          if (TOOL_CALLING_LOG) console.error('[SMS] Send error:', error);
          setSendError();
        }
      }
    )();
  };

  // SMS 개별 발송 (individual 모드) //
  const handleIndividualSmsSend = (messageId, memberId, text) => {
    if (TOOL_CALLING_LOG) console.log('[SMS] Individual send:', { messageId, memberId, text });

    // 발송 중 상태로 업데이트 //
    updateAiMessage(messageId, undefined, {
      smsRecommendation: prev => ({
        ...prev,
        individualTexts: {
          ...prev.individualTexts,
          [memberId]: { text: text || prev.individualTexts?.[memberId]?.text || '', status: 'sending' },
        },
      }),
    });

    // 발송 실패 → 에러 상태 //
    const setSendError = () => {
      updateAiMessage(messageId, undefined, {
        smsRecommendation: prev => ({
          ...prev,
          individualTexts: {
            ...prev.individualTexts,
            [memberId]: { text: text || prev.individualTexts?.[memberId]?.text || '', status: 'error' },
          },
        }),
      });
    };

    // SMS 발송 API 호출 — message/send_target (title, content, memberIds 단건) //
    const memberName = chatMessagesRef.current.find(m => m.id === messageId)?.smsRecommendation?.recipients?.find(r => r.memberId === memberId)?.name;
    const fetchModule = new FetchModule();
    (
      async () => {
        try {
          const responseData = await fetchModule.postDataVersion2('message/send_target', 'POST', {
            title: storeName,
            content: normalizeSmsContent(text, memberName),
            memberIds: [memberId],
          });

          if (responseData.status === 200) {
            // 발송 성공 → 상태 업데이트 //
            updateAiMessage(messageId, undefined, {
              smsRecommendation: prev => ({
                ...prev,
                individualTexts: {
                  ...prev.individualTexts,
                  [memberId]: { text: text || prev.individualTexts?.[memberId]?.text || '', status: 'sent' },
                },
              }),
            });

            // conversation-log 저장 //
            const currentSms = chatMessagesRef.current.find(m => m.id === messageId)?.smsRecommendation;

            if (currentSms) {
              updateConversationSms(messageId, {
                ...currentSms,
                individualTexts: {
                  ...currentSms.individualTexts,
                  [memberId]: { text: text || currentSms.individualTexts?.[memberId]?.text || '', status: 'sent' },
                },
              });
            }
          }
          else setSendError();
        }
        catch (error) {
          if (TOOL_CALLING_LOG) console.error('[SMS] Individual send error:', error);
          setSendError();
        }
      }
    )();
  };

    return {
    // 저장 //
    updateConversationSms,
    // 생성 //
    generateSmsTemplates,
    handleIndividualSmsGenerate,
    // 발송 //
    handleSmsSend,
    handleIndividualSmsSend,
  };
}